// src/app/components/admin/aimode-backup-setting.tsx
//
// Platform section of Global Settings: SerpApi as the backup provider for the
// Google AI Mode lane (#89).
//
// DataForSEO fetches every AI Mode answer. When it is down longer than its own
// retries, calls fail and the run is left with holes (one run lost 174 of 250).
// With a backup active, each call DataForSEO fails is tried once on SerpApi,
// under the same country/language/device, and the row records which provider
// answered.
//
// The backup is used only when it is switched on AND the stored key has
// validated. A new key is checked with SerpApi before it is saved (that check
// is free — it uses no searches), and switching on re-checks the stored key.

import { useEffect, useState } from "react";
import { CheckCircle2, KeyRound, Loader2, ShieldAlert } from "lucide-react";

import { aimodeBackupApi, type AiModeBackupSettings, type AiModeBackupUpdate } from "@/api/aimode-backup";

const muted = { fontSize: "var(--text-xs)", color: "var(--muted-foreground)", lineHeight: 1.5 } as const;

function formatUsd(value: number | null | undefined) {
  return typeof value === "number" ? `$${value.toFixed(4)}` : "—";
}

export function AiModeBackupSetting() {
  const [settings, setSettings] = useState<AiModeBackupSettings | null>(null);
  const [keyDraft, setKeyDraft] = useState("");
  const [costDraft, setCostDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");

  function adopt(next: AiModeBackupSettings) {
    setSettings(next);
    setCostDraft(next.cost_per_search_usd == null ? "" : String(next.cost_per_search_usd));
  }

  useEffect(() => {
    let alive = true;
    aimodeBackupApi
      .get()
      .then((next) => alive && adopt(next))
      .catch((e) => alive && setErr(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function run(label: string, action: () => Promise<AiModeBackupSettings>, done: string) {
    if (busy) return;
    setBusy(label);
    setErr("");
    setNotice("");
    try {
      adopt(await action());
      setNotice(done);
    } catch (e: any) {
      setErr(String(e?.message || e));
      // A failed validation is still recorded server-side; show what is stored.
      aimodeBackupApi.get().then(adopt).catch(() => undefined);
    } finally {
      setBusy(null);
    }
  }

  const disabled = loading || busy !== null;
  const save = (changes: AiModeBackupUpdate, done: string) =>
    run("save", () => aimodeBackupApi.update(changes), done);

  const costValue = costDraft.trim() === "" ? null : Number.parseFloat(costDraft);
  const costInvalid =
    costValue !== null &&
    (!Number.isFinite(costValue) ||
      (settings != null &&
        (costValue < settings.limits.min_cost_per_search_usd ||
          costValue > settings.limits.max_cost_per_search_usd)));
  const costDirty =
    !!settings && !costInvalid && costValue !== (settings.cost_per_search_usd ?? null);

  const validation = settings?.validation;
  const account = validation?.account;

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
          Google AI Mode backup provider
        </h2>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
          DataForSEO fetches every Google AI Mode answer. When it fails after its own retries, the call
          can be tried once on SerpApi instead, under the same country, language and device. Each
          answer records which provider fetched it. The backup is used only when it is switched on and
          its key has been validated. A brand can use its own SerpApi key instead, set on the Brand API
          Keys page; brands without one use this platform key.
        </p>

        {err && (
          <div
            className="mt-4 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--error-text)", backgroundColor: "var(--error-bg)" }}
          >
            {err}
          </div>
        )}

        {settings && (
          <>
            {/* Status */}
            <div
              className="mt-5 flex items-start gap-3 rounded-xl border p-4"
              style={{ borderColor: settings.active ? "var(--alpha-teal)" : "var(--border)" }}
            >
              {settings.active ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: "var(--alpha-teal)" }} />
              ) : (
                <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: "var(--muted-foreground)" }} />
              )}
              <div className="min-w-0 flex-1">
                <div style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
                  {settings.active
                    ? "Active — failed DataForSEO calls go to SerpApi"
                    : settings.enabled
                      ? "Switched on, but not active — the key has not validated"
                      : "Off — failed DataForSEO calls stay failed"}
                </div>
                <p className="mt-1" style={muted}>
                  {settings.key_set ? `Key ending in ${settings.key_last4}` : "No key stored"}
                  {validation &&
                    ` · last checked ${new Date(validation.checked_at).toLocaleString()}: ${validation.message}`}
                </p>
                {account && validation?.ok && (
                  <p className="mt-1" style={muted}>
                    {account.plan_name ?? "Plan unknown"}
                    {account.account_email ? ` · ${account.account_email}` : ""}
                    {typeof account.total_searches_left === "number"
                      ? ` · ${account.total_searches_left.toLocaleString()} searches left`
                      : ""}
                  </p>
                )}
              </div>
              <button
                type="button"
                disabled={disabled || !settings.key_set}
                onClick={() =>
                  save(
                    { enabled: !settings.enabled },
                    settings.enabled ? "Backup switched off." : "Key re-checked and backup switched on.",
                  )
                }
                className="flex-shrink-0 rounded-lg px-3 py-2"
                style={{
                  cursor: disabled || !settings.key_set ? "default" : "pointer",
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-weight-medium)",
                  color: settings.enabled ? "var(--foreground)" : "#fff",
                  backgroundColor: settings.enabled
                    ? "var(--muted, #f1f5f9)"
                    : settings.key_set
                      ? "var(--alpha-teal)"
                      : "var(--muted, #f1f5f9)",
                  opacity: settings.key_set ? 1 : 0.6,
                }}
              >
                {busy === "save" && <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" />}
                {settings.enabled ? "Switch off" : "Switch on"}
              </button>
            </div>

            {/* Key */}
            <div className="mt-6">
              <label
                htmlFor="aimode-backup-key"
                style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)", color: "var(--foreground)" }}
              >
                SerpApi API key
              </label>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span
                  className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: "var(--muted, #f1f5f9)", color: "var(--muted-foreground)" }}
                >
                  <KeyRound className="h-4.5 w-4.5" />
                </span>
                <input
                  id="aimode-backup-key"
                  type="password"
                  autoComplete="off"
                  placeholder={settings.key_set ? `•••• ${settings.key_last4} (enter a new key to replace)` : "Paste the key"}
                  value={keyDraft}
                  disabled={disabled}
                  onChange={(e) => setKeyDraft(e.target.value)}
                  className="min-w-0 flex-1 rounded-lg border px-3 py-2"
                  style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", backgroundColor: "var(--card, transparent)", borderColor: "var(--border)" }}
                />
                <button
                  type="button"
                  disabled={disabled || keyDraft.trim() === ""}
                  onClick={async () => {
                    await save({ api_key: keyDraft.trim() }, "Key validated and saved.");
                    setKeyDraft("");
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2"
                  style={{
                    cursor: disabled || keyDraft.trim() === "" ? "default" : "pointer",
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-medium)",
                    color: keyDraft.trim() ? "#fff" : "var(--muted-foreground)",
                    backgroundColor: keyDraft.trim() ? "var(--alpha-teal)" : "var(--muted, #f1f5f9)",
                  }}
                >
                  {busy === "save" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Validate &amp; save
                </button>
              </div>
              <p className="mt-1.5" style={{ ...muted, paddingLeft: 44 }}>
                The key is checked with SerpApi before it is saved. That check uses no searches. A key that is
                rejected is not saved, and any key already stored stays in place. Stored keys are encrypted.
              </p>
              {settings.key_set && (
                <div className="mt-2 flex flex-wrap gap-2" style={{ paddingLeft: 44 }}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => run("validate", () => aimodeBackupApi.validate(), "Key re-checked.")}
                    className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5"
                    style={{ cursor: disabled ? "default" : "pointer", fontSize: "var(--text-xs)", borderColor: "var(--border)", color: "var(--foreground)" }}
                  >
                    {busy === "validate" && <Loader2 className="h-3 w-3 animate-spin" />}
                    Re-check key
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (window.confirm("Remove the SerpApi key? The backup will be switched off.")) {
                        save({ clear_key: true }, "Key removed and backup switched off.");
                      }
                    }}
                    className="rounded-lg border px-3 py-1.5"
                    style={{ cursor: disabled ? "default" : "pointer", fontSize: "var(--text-xs)", borderColor: "var(--border)", color: "var(--error-text)" }}
                  >
                    Remove key
                  </button>
                </div>
              )}
            </div>

            {/* Cost */}
            <div className="mt-6">
              <label
                htmlFor="aimode-backup-cost"
                style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)", color: "var(--foreground)" }}
              >
                Cost per search
              </label>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  id="aimode-backup-cost"
                  type="number"
                  step={0.001}
                  min={settings.limits.min_cost_per_search_usd}
                  max={settings.limits.max_cost_per_search_usd}
                  placeholder={
                    account?.plan_cost_per_search_usd != null
                      ? `${account.plan_cost_per_search_usd} (from plan)`
                      : "USD"
                  }
                  value={costDraft}
                  disabled={disabled}
                  onChange={(e) => setCostDraft(e.target.value)}
                  className="w-44 rounded-lg border px-3 py-2"
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--foreground)",
                    backgroundColor: "var(--card, transparent)",
                    borderColor: costInvalid ? "var(--error-border)" : "var(--border)",
                  }}
                />
                <button
                  type="button"
                  disabled={disabled || !costDirty}
                  onClick={() => save({ cost_per_search_usd: costValue }, "Cost per search saved.")}
                  className="rounded-lg px-3 py-2"
                  style={{
                    cursor: disabled || !costDirty ? "default" : "pointer",
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-medium)",
                    color: costDirty ? "#fff" : "var(--muted-foreground)",
                    backgroundColor: costDirty ? "var(--alpha-teal)" : "var(--muted, #f1f5f9)",
                  }}
                >
                  Save
                </button>
                <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                  In use: {formatUsd(settings.effective_cost_per_search_usd)}
                </span>
              </div>
              <p className="mt-1.5" style={muted}>
                SerpApi does not report a price for each search, so this figure is what a backup answer adds to
                a run's cost. Leave it empty to use the plan's monthly price divided by its monthly searches.
              </p>
            </div>
          </>
        )}

        {notice && !err && (
          <p className="mt-4" style={muted}>
            {notice} Running measurements pick up a change within about a minute.
          </p>
        )}
      </div>
    </div>
  );
}

export default AiModeBackupSetting;
